import styled, { useTheme } from "styled-components/macro";
import { UserIcon } from "../Icons/Icons";

export type AvatarType = "text" | "unassigned";
export type AvatarSize = "small" | "medium";

interface AvatarProps {
  /** Display name used to derive initials (`type="text"` only). */
  name?: string;
  size?: AvatarSize;
  /** `text` shows initials; `unassigned` shows a neutral user placeholder. */
  type?: AvatarType;
}

interface StyledAvatarProps {
  $size: AvatarSize;
  $type: AvatarType;
}

const sizePx: Record<AvatarSize, number> = {
  small: 24,
  medium: 28,
};

const iconSizePx: Record<AvatarSize, number> = {
  small: 12,
  medium: 16,
};

const AvatarRoot = styled.div<StyledAvatarProps>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme, $type }) =>
      $type === "unassigned"
        ? theme.resolve("color_border_lighter")
        : theme.resolve("color_border_brand")};
  background-color: ${({ theme, $type }) =>
    $type === "unassigned"
      ? theme.resolve("color_bg_surface_standard")
      : theme.resolve("color_bg_surface_brand3")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_xs")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
  line-height: ${({ theme }) => theme.resolve("line_height_xs")};
  width: ${({ $size }) => sizePx[$size]}px;
  height: ${({ $size }) => sizePx[$size]}px;
`;

export function getInitials(name: string): string {
  if (!name) {
    return "U";
  }
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

/**
 * User/entity identity avatar.
 * Size `small` = Design System Size M (24px); `medium` ≈ dense L (28px).
 */
export const Avatar = ({
  name = "",
  size = "medium",
  type = "text",
}: AvatarProps) => {
  const theme = useTheme();

  return (
    <AvatarRoot $size={size} $type={type} aria-hidden>
      {type === "unassigned" ? (
        <UserIcon
          width={iconSizePx[size]}
          height={iconSizePx[size]}
          fill={theme.resolve("color_icon_tertiary")}
        />
      ) : (
        getInitials(name)
      )}
    </AvatarRoot>
  );
};
